import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import serviceImage1 from "../assets/serviceImage1.jpg";
import serviceImage2 from "../assets/serviceImage2.jpg";
import serviceImage3 from "../assets/serviceImage3.jpg";
import serviceImage4 from "../assets/serviceImage4.jpg";
import offeringsIcon from "../assets/offerings.svg";

import "./Services.css";

const serviceData = (t) => [
  {
    title: t("services.sections.masterTheRoads.title"),
    description: t("services.sections.masterTheRoads.description"),
    image: serviceImage1,
    offerings: [
      {
        icon: offeringsIcon,
        heading: t("services.sections.masterTheRoads.offerings.basics.heading"),
        text: t("services.sections.masterTheRoads.offerings.basics.text"),
        price: t("services.sections.masterTheRoads.offerings.basics.price"),
        note: t("services.sections.masterTheRoads.offerings.basics.note"),
      },
    ],
  },
  {
    title: t("services.sections.conquerTheTrack.title"),
    description: t("services.sections.conquerTheTrack.description"),
    image: serviceImage2,
    offerings: [
      {
        icon: offeringsIcon,
        heading: t("services.sections.conquerTheTrack.offerings.Most.heading"),
        text: t("services.sections.conquerTheTrack.offerings.Most.text"),
        price: t("services.sections.conquerTheTrack.offerings.Most.price"),
      },
      {
        icon: offeringsIcon,
        heading: t("services.sections.conquerTheTrack.offerings.Brno.heading"),
        text: t("services.sections.conquerTheTrack.offerings.Brno.text"),
        price: t("services.sections.conquerTheTrack.offerings.Brno.price"),
      },
      {
        icon: offeringsIcon,
        heading: t("services.sections.conquerTheTrack.offerings.Sosnova.heading"),
        text: t("services.sections.conquerTheTrack.offerings.Sosnova.text"),
        price: t("services.sections.conquerTheTrack.offerings.Sosnova.price"),
        note: t("services.sections.conquerTheTrack.offerings.Sosnova.note"),
      },
    ],
  },
  {
    title: t("services.sections.understandTheMachine.title"),
    description: t("services.sections.understandTheMachine.description"),
    image: serviceImage3,
    offerings: [
      {
        icon: offeringsIcon,
        heading: t("services.sections.understandTheMachine.offerings.consultation.heading"),
        text: t("services.sections.understandTheMachine.offerings.consultation.text"),
        price: t("services.sections.understandTheMachine.offerings.consultation.price"),
        note: t("services.sections.understandTheMachine.offerings.consultation.note"),
      },
    ],
  },
  {
    title: t("services.sections.driveBeyondLimits.title"),
    description: t("services.sections.driveBeyondLimits.description"),
    image: serviceImage4,
    offerings: [
      {
        icon: offeringsIcon,
        heading: t("services.sections.driveBeyondLimits.offerings.nurburgring.heading"),
        text: t("services.sections.driveBeyondLimits.offerings.nurburgring.text"),
        price: t("services.sections.driveBeyondLimits.offerings.nurburgring.price"),
        note: t("services.sections.driveBeyondLimits.offerings.nurburgring.note"),
      },
    ],
  },
];

export default function Services() {
  const { t, i18n } = useTranslation();

  const scrollRef = useRef(null);
  const mainRefs = useRef([]);
  const rafRef = useRef(null);

  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  const [desktopMainHeight, setDesktopMainHeight] = useState(null);

  const items = useMemo(() => serviceData(t), [t]);

  const isDesktop = () => window.matchMedia("(min-width: 769px)").matches;

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setIsAtStart(el.scrollLeft <= 0);
    setIsAtEnd(el.scrollLeft + el.offsetWidth >= el.scrollWidth - 1);
  }, []);

  const scroll = (dir) => {
    const container = scrollRef.current;
    if (!container) return;

    const card = container.querySelector(".service-card");
    if (!card) return;

    const gap = 24;
    const scrollAmount = card.offsetWidth + gap;

    container.scrollBy({
      left: dir === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const measureMaxMainHeight = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    if (!isDesktop()) {
      setDesktopMainHeight(null);
      return;
    }

    setDesktopMainHeight(null);

    rafRef.current = requestAnimationFrame(() => {
      const heights = mainRefs.current
        .filter(Boolean)
        .map((el) => el.getBoundingClientRect().height);

      if (!heights.length) return;

      const max = Math.ceil(Math.max(...heights));
      setDesktopMainHeight(max);
      updateScrollState();
    });
  }, [updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState, { passive: true });
    updateScrollState();

    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  useEffect(() => {
    measureMaxMainHeight();

    const onResize = () => measureMaxMainHeight();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [measureMaxMainHeight, i18n.language, items]);

  return (
    <section className="services-section">
      <div className="container">
        <div className="services-header">
          <div className="header-row">
            <h2>{t("services.header")}</h2>
          </div>
          <p>{t("services.intro")}</p>
        </div>

        <div className="services-carousel">
          <button
            className={`carousel-arrow left ${isAtStart ? "disabled" : ""}`}
            onClick={() => scroll("left")}
            disabled={isAtStart}
            aria-label="Previous"
          >
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="carousel-track" ref={scrollRef}>
            {items.map((item, index) => (
              <div className="service-card" key={index}>
                <div
                  className="service-main"
                  ref={(el) => (mainRefs.current[index] = el)}
                  style={desktopMainHeight ? { height: `${desktopMainHeight}px` } : undefined}
                >
                  <div className="service-card-image-wrapper">
                    <img src={item.image} alt={item.title} onLoad={measureMaxMainHeight} />
                    <h4 className="service-card-title">{item.title}</h4>
                  </div>

                  <p className="service-description">{item.description}</p>
                </div>

                <div className="service-offerings-card">
                  <div className="service-offerings">
                    {item.offerings?.map((o, i) => (
                      <div className="service-offering" key={i}>
                        <div className="service-offering-head">
                          <img
                            className="service-offering-icon"
                            src={o.icon}
                            alt=""
                            aria-hidden="true"
                          />
                          <h5 className="service-offering-title">{o.heading}</h5>
                        </div>

                        <p className="service-offering-text">{o.text}</p>
                        <div className="service-offering-price">{o.price}</div>
                        {o.note ? <div className="service-offering-note">{o.note}</div> : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            className={`carousel-arrow right ${isAtEnd ? "disabled" : ""}`}
            onClick={() => scroll("right")}
            disabled={isAtEnd}
            aria-label="Next"
          >
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path
                d="M9 6L15 12L9 18"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
